import { createApp } from 'vue';
import type { App, Component } from 'vue';
import { lang, langChoice } from '@/lang/composable';
import { useComponents } from '@/common';
import { Modular } from '@/Modular';

/**
 * Live-demo islands: server-rendered manual HTML carries
 * `[data-manual-embed]` placeholders ({component, props} JSON); after the
 * v-html paints we mount the registered Vue component into each as its own
 * mini app (Chat-widget pattern — the components must be self-contained,
 * which DataGrid is: window.axios + global route()).
 */

const registry = new Map<string, Component>();

/**
 * Register an embeddable component under the name the server emits.
 */
export function registerEmbedComponent(name: string, component: Component): void {
    registry.set(name, component);
}

export function useManualEmbeds() {
    const apps: App[] = [];

    function mountEmbeds(container: HTMLElement | null): void {
        unmountEmbeds();

        if (!container) {
            return;
        }

        container.querySelectorAll<HTMLElement>('[data-manual-embed]').forEach((element) => {
            try {
                const payload = JSON.parse(element.dataset.manualEmbed ?? '{}');
                const component = registry.get(payload.component as string);

                if (!component) {
                    return;
                }

                const island = createApp(component, payload.props ?? {});
                // Mirror the main admin app globals (mixins, theme components,
                // module registrations) — grid cells resolve by global name.
                island.mixin({ methods: { route: window.route, lang, langChoice } });
                useComponents(island as App<Element>);
                new Modular(island);
                island.mount(element);
                apps.push(island);
            } catch {
                // malformed payload — leave the skeleton fallback in place
            }
        });
    }

    function unmountEmbeds(): void {
        while (apps.length) {
            apps.pop()?.unmount();
        }
    }

    return { mountEmbeds, unmountEmbeds };
}
